import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { defineMessages, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import noop from 'lodash/noop';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import {
  Menu,
  MenuButton,
  MenuItem,
} from '@lifeomic/chroma-react/components/Menu';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { ChevronDown } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import useSurveysList, {
  UseSurveysListProvider,
} from '@lifeomic/phc-web-toolkit/src/components/ManageSurveys/SurveysList/useSurveysList';
import Survey from '@lifeomic/phc-web-toolkit/src/components/Surveys/Components/Survey';
import FullPageContainer from '@lifeomic/phc-web-toolkit/src/components/Surveys/FullPageContainer';
import questionnaireUtils from '@lifeomic/phc-web-toolkit/src/components/Surveys/util/questionnaireUtils';
import { useSurveysAndCount } from '@lifeomic/phc-web-toolkit/src/components/ManageSurveys/SurveysList/useSurveysAndCount';
import { useSurveyResponses } from '@lifeomic/phc-web-toolkit/src/hooks/useSurveyResponses';
import { useInProgressSurveyStart } from '@lifeomic/phc-web-toolkit/src/components/Surveys/hooks/useInProgressSurveyStart';
import InProgressSurveyChoiceModal from '@lifeomic/phc-web-toolkit/src/components/Surveys/Components/InProgressSurveyChoiceModal';
import {
  dedupeClinicQuickLaunchSurveys,
  getClinicSurveyLaunchStatus,
  getQuickLaunchLabel,
  hasQuickLaunchExtension,
  sortClinicQuickLaunchSurveys,
} from '@lifeomic/phc-web-toolkit/src/components/Surveys/util/getClinicSurveyLaunchStatus';
import { userSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/user';
import { useDispatch, useSelector } from 'react-redux';
import urls from '../../../../util/urls';
import {
  ReopenCompletedSurveyDialog,
  reopenCompletedResponse,
  saveReopenedResponse,
} from '../../../surveys/reopenCompletedSurvey';

// Soft scan of the project survey list (page size 10). Flagged surveys past
// this window never appear — FHIR/ES has no cheap filter on the quick-launch
// modifierExtension, so a targeted query is not available from this client.
const MAX_SURVEY_PAGES = 15;

const messages = defineMessages({
  title: {
    id: 'MemberDetail.clinicSurveyQuickLaunch.title',
    defaultMessage: 'Clinic surveys',
  },
  subtitle: {
    id: 'MemberDetail.clinicSurveyQuickLaunch.subtitle',
    defaultMessage: 'Quickly launch selected clinic surveys and forms.',
  },
  launch: {
    id: 'MemberDetail.clinicSurveyQuickLaunch.launch',
    defaultMessage: 'Launch',
  },
  view: {
    id: 'MemberDetail.clinicSurveyQuickLaunch.view',
    defaultMessage: 'View',
  },
  viewWithDate: {
    id: 'MemberDetail.clinicSurveyQuickLaunch.viewWithDate',
    defaultMessage: 'View · {when}',
  },
  viewTooltip: {
    id: 'MemberDetail.clinicSurveyQuickLaunch.viewTooltip',
    defaultMessage: 'View the completed response (read-only)',
  },
  viewMenuLabel: {
    id: 'MemberDetail.clinicSurveyQuickLaunch.viewMenuLabel',
    defaultMessage: 'View a completed response (read-only)',
  },
  notStarted: {
    id: 'MemberDetail.clinicSurveyQuickLaunch.notStarted',
    defaultMessage: 'Not started',
  },
  inProgress: {
    id: 'MemberDetail.clinicSurveyQuickLaunch.inProgress',
    defaultMessage: 'In progress · last edited {when}',
  },
  completed: {
    id: 'MemberDetail.clinicSurveyQuickLaunch.completed',
    defaultMessage: 'Completed {when}',
  },
  empty: {
    id: 'MemberDetail.clinicSurveyQuickLaunch.empty',
    defaultMessage: 'No clinic quick-launch surveys in this project yet.',
  },
});

const useStyles = makeStyles(theme => ({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  // Match MemberDetail landingSectionHeader / landingSectionSubtitle.
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  rows: {
    display: 'grid',
    // Cap column width so rows do not stretch across the full Quick Access /
    // Other tools content width. auto-fill stacks on narrow viewports.
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 340px))',
    gap: theme.spacing(1),
    width: '100%',
    justifyContent: 'start',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.pxToRem(8),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    minWidth: 0,
  },
  copy: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.125),
  },
  title: {
    lineHeight: 1.25,
  },
  status: {
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  launchButton: {
    flexShrink: 0,
    minHeight: theme.pxToRem(32),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    fontSize: theme.pxToRem(13),
  },
  viewButton: {
    flexShrink: 0,
    minHeight: theme.pxToRem(32),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: theme.pxToRem(13),
    whiteSpace: 'nowrap',
  },
  loading: {
    padding: theme.spacing(1.5, 0),
  },
}));

const formatWhen = (iso?: string) =>
  iso ? moment(iso).format('ll LT') : undefined;

// Compact completion date for the View affordance ("Sep 18, 2026"). The year
// is always shown: renewing members accumulate one completion per year, so
// same-month dates from different years must stay distinguishable.
const formatViewWhen = (iso?: string): string | undefined => {
  if (!iso) {
    return undefined;
  }
  const parsed = moment(iso);
  if (!parsed.isValid()) {
    return undefined;
  }
  return parsed.format('MMM D, YYYY');
};

type ClinicSurveyViewTarget = {
  survey: fhir.Questionnaire;
  response: fhir.QuestionnaireResponse;
};

type ClinicSurveyQuickLaunchTableProps = {
  memberId: string;
};

const ClinicSurveyQuickLaunchTable: React.FC<
  ClinicSurveyQuickLaunchTableProps
> = ({ memberId }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const history = useHistory();
  const currentUserId = useSelector(
    (state: any) => userSelectors.selectActiveUser(state)?.id,
  );
  const { setStatusFilters } = useSurveysList();
  const { results, isLoading, hasMoreToLoad, loadMore } = useSurveysAndCount();
  const {
    responses,
    hasFetchedResponses,
    fetchResponsesError,
    updateResponse,
  } = useSurveyResponses({
    patientId: memberId,
  });
  const dispatch = useDispatch();
  const [isReopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  const [isReopening, setReopening] = useState(false);
  const [pagesLoaded, setPagesLoaded] = useState(0);
  const scanCapReached = pagesLoaded >= MAX_SURVEY_PAGES;
  const [viewTarget, setViewTarget] = useState<ClinicSurveyViewTarget | null>(
    null,
  );

  useEffect(() => {
    setStatusFilters(['active']);
  }, [setStatusFilters]);

  useEffect(() => {
    if (isLoading || !hasMoreToLoad || scanCapReached) {
      return;
    }
    setPagesLoaded(loaded => loaded + 1);
    void loadMore();
  }, [hasMoreToLoad, isLoading, loadMore, results.length, scanCapReached]);

  const surveys = useMemo(() => {
    const quickLaunch = (results as fhir.Questionnaire[]).filter(
      questionnaire => hasQuickLaunchExtension(questionnaire),
    );
    // Author-declared weight on the quick-launch extension, unweighted last,
    // title tie-break — keeps order stable as paginated scans append.
    return sortClinicQuickLaunchSurveys(
      dedupeClinicQuickLaunchSurveys(quickLaunch, responses),
    );
  }, [results, responses]);

  const {
    isPromptOpen,
    pendingDrafts,
    requestStart,
    dismiss,
    confirmContinue,
    confirmStartNew,
  } = useInProgressSurveyStart({
    responses,
    areResponsesReady: hasFetchedResponses && !fetchResponsesError,
    onStartNew: survey => {
      if (!survey.id) {
        return;
      }
      history.push(
        `${urls.app.members(memberId)}/surveys?launch=${encodeURIComponent(
          survey.id,
        )}`,
      );
    },
    onContinue: (survey, response) => {
      if (!survey.id || !response.id) {
        return;
      }
      history.push(
        `${urls.app.members(memberId)}/surveys?launch=${encodeURIComponent(
          survey.id,
        )}&response=${encodeURIComponent(response.id)}`,
      );
    },
  });

  // Render the response against the questionnaire version it references when
  // that version is in the scanned results (dedupe may have picked a sibling
  // version for the row); fall back to the row's questionnaire otherwise.
  const viewQuestionnaire = useMemo(() => {
    if (!viewTarget) {
      return null;
    }
    const responseSurveyId = questionnaireUtils.surveyIdFromResponse(
      viewTarget.response,
    );
    return (
      (results as fhir.Questionnaire[]).find(
        questionnaire => questionnaire.id === responseSurveyId,
      ) ?? viewTarget.survey
    );
  }, [results, viewTarget]);

  const closeViewer = () => {
    setViewTarget(null);
    setReopenConfirmOpen(false);
  };
  const canReopen = Boolean(
    viewQuestionnaire &&
      questionnaireUtils.allowsResponseResubmission(viewQuestionnaire),
  );
  const confirmReopen = async () => {
    if (!viewTarget?.response.id || !viewTarget.survey.id || isReopening) {
      return;
    }
    const reopened = reopenCompletedResponse(viewTarget.response, uuid());
    setReopening(true);
    const saved: boolean = await dispatch(
      saveReopenedResponse(reopened) as any,
    );
    setReopening(false);
    setReopenConfirmOpen(false);
    if (!saved) {
      return;
    }
    updateResponse(reopened);
    history.push(
      `${urls.app.members(memberId)}/surveys?launch=${encodeURIComponent(
        viewTarget.survey.id,
      )}&response=${encodeURIComponent(reopened.id!)}`,
    );
  };

  const stillScanning = isLoading || (hasMoreToLoad && !scanCapReached);

  if (surveys.length === 0 && stillScanning) {
    return (
      <Box className={classes.loading} align="center">
        <DotLoader />
      </Box>
    );
  }

  if (surveys.length === 0) {
    return (
      <Text size="caption" className={classes.status}>
        {formatMessage(messages.empty)}
      </Text>
    );
  }

  return (
    <>
      <div className={classes.rows}>
        {surveys.map(survey => {
          const status = getClinicSurveyLaunchStatus(survey, responses);
          const statusText =
            status.kind === 'in-progress'
              ? formatMessage(messages.inProgress, {
                  when: formatWhen(status.lastEditedAt) ?? '',
                })
              : status.kind === 'completed'
              ? formatMessage(messages.completed, {
                  when: formatWhen(status.completedAt) ?? '',
                })
              : formatMessage(messages.notStarted);

          const completedResponses = status.completedResponses;
          const latestCompleted = completedResponses[0];
          const viewWhen = formatViewWhen(latestCompleted?.authored);

          return (
            <div className={classes.row} key={survey.id ?? survey.title}>
              <div className={classes.copy}>
                <Text size="subbody" weight="bold" className={classes.title}>
                  {getQuickLaunchLabel(survey) ?? survey.title}
                </Text>
                <Text size="caption" className={classes.status}>
                  {statusText}
                </Text>
              </div>
              <div className={classes.actions}>
                {completedResponses.length === 1 && latestCompleted && (
                  <Tooltip title={formatMessage(messages.viewTooltip)}>
                    <Button
                      variant="text"
                      className={classes.viewButton}
                      onClick={() =>
                        setViewTarget({ survey, response: latestCompleted })
                      }
                    >
                      {viewWhen
                        ? formatMessage(messages.viewWithDate, {
                            when: viewWhen,
                          })
                        : formatMessage(messages.view)}
                    </Button>
                  </Tooltip>
                )}
                {/* Renewing members complete these surveys annually, so a
                    handful of completions accumulate per row. List every one
                    (counts stay small — one per year) newest first; each item
                    opens the same read-only viewer. Menu clones its anchor
                    with disclosure props, so the MenuButton cannot be wrapped
                    in the Tooltip the single-response button uses. */}
                {completedResponses.length > 1 && (
                  <Menu
                    aria-label={formatMessage(messages.viewMenuLabel)}
                    anchorElement={
                      <MenuButton
                        variant="text"
                        className={classes.viewButton}
                        trailingIcon={ChevronDown}
                      >
                        {formatMessage(messages.view)}
                      </MenuButton>
                    }
                    items={completedResponses.map((response, index) => (
                      <MenuItem
                        key={response.id ?? index}
                        text={
                          formatViewWhen(response.authored) ??
                          formatMessage(messages.view)
                        }
                        onClick={() => setViewTarget({ survey, response })}
                      />
                    ))}
                  />
                )}
                <Tooltip title={survey.description || statusText}>
                  <Button
                    variant="contained"
                    className={classes.launchButton}
                    disabled={!hasFetchedResponses || !!fetchResponsesError}
                    onClick={() => requestStart(survey)}
                  >
                    {formatMessage(messages.launch)}
                  </Button>
                </Tooltip>
              </div>
            </div>
          );
        })}
      </div>
      <InProgressSurveyChoiceModal
        isOpen={isPromptOpen}
        drafts={pendingDrafts}
        currentUserId={currentUserId}
        onDismiss={dismiss}
        onContinue={confirmContinue}
        onStartNew={confirmStartNew}
      />
      {/* Read-only viewer for a completed response. Survey blocks every
          write path when isReadOnly, so onResponseUpdate is a no-op and
          onComplete only closes the modal. templatePreviewEnabled follows the
          ResponseList/SurveyList pattern: Survey itself gates the preview on
          the questionnaire's hide-template-preview tag and the viewer's
          survey-template ABAC permissions, so the eye-icon toggle only
          appears where it would elsewhere. */}
      <FullPageContainer
        isOpen={!!viewTarget}
        onClose={closeViewer}
        title={viewTarget?.survey.title}
      >
        {viewTarget && viewQuestionnaire && (
          <>
            {canReopen && (
              <Box direction="row" justify="flex-end" padding={1}>
                <Button
                  variant="outlined"
                  onClick={() => setReopenConfirmOpen(true)}
                >
                  Reopen
                </Button>
              </Box>
            )}
            <Survey
              isReadOnly
              questionnaire={viewQuestionnaire}
              questionnaireResponse={viewTarget.response}
              onResponseUpdate={noop}
              onComplete={closeViewer}
              autoSave={false}
              templatePreviewEnabled
              subjectId={memberId}
            />
          </>
        )}
      </FullPageContainer>
      <ReopenCompletedSurveyDialog
        isOpen={isReopenConfirmOpen}
        isSubmitting={isReopening}
        onCancel={() => setReopenConfirmOpen(false)}
        onConfirm={() => {
          void confirmReopen();
        }}
      />
    </>
  );
};

export const ClinicSurveyQuickLaunch: React.FC<{
  memberId?: string;
}> = ({ memberId }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  if (!memberId) {
    return null;
  }

  return (
    <section className={classes.section}>
      <div className={classes.header}>
        <Text size="body" weight="bold">
          {formatMessage(messages.title)}
        </Text>
        <Text size="caption" className={classes.subtitle}>
          {formatMessage(messages.subtitle)}
        </Text>
      </div>
      <UseSurveysListProvider>
        <ClinicSurveyQuickLaunchTable memberId={memberId} />
      </UseSurveysListProvider>
    </section>
  );
};
